// "Costo de los paros" — barras apiladas por causal MADRE por bucket.
//
// Sigue los filtros del gráfico central (período, planta, turno, granularidad).
// $ = valor no producido (fórmula C6). Sin puede_ver_costos muestra horas.
// Las causales se colapsan a las top-3 del período + "Otras".
//
// COLORES: cada categoría de paro tiene su color configurado por el tenant
// (categorias_paro.color, pantalla A3) — el endpoint lo trae por causal madre
// y acá se respeta; la paleta del proyecto es solo fallback.
//
// El TOTAL de cada barra va arriba del stack vía una Line invisible con
// dataKey="total" (etiquetar el último <Bar> es frágil: su tope no siempre es
// el tope visual del stack).

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ParoCausalPunto } from 'schemas';

const OTRAS = 'Otras';
// Fallback cuando la categoría no tiene color configurado.
const FALLBACK_FILLS = [
  'var(--color-crit)',
  'var(--color-warn)',
  'var(--color-info)',
  'var(--color-fg-tertiary)',
];

function copM(n: number): string {
  if (Math.abs(n) >= 1_000_000)
    return `$${(n / 1_000_000).toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} M`;
  return `$${new Intl.NumberFormat('es-CO').format(Math.round(n))}`;
}

function horas(min: number): string {
  return `${(min / 60).toLocaleString('es-CO', { maximumFractionDigits: 1 })} h`;
}

export function CostoParosChart({
  puntos,
  labelDe,
  conCostos,
}: {
  /** Ya filtrados por planta; el componente agrupa por bucket y causal. */
  puntos: ParoCausalPunto[];
  labelDe: (bucket: string) => string;
  conCostos: boolean;
}) {
  // Top-3 causales del período por valor (u horas sin costos) + "Otras".
  const porCausal = new Map<string, number>();
  const colorDeCausal = new Map<string, string>();
  for (const p of puntos) {
    const peso = conCostos ? (p.valor_cop ?? 0) : p.minutos;
    porCausal.set(p.causal, (porCausal.get(p.causal) ?? 0) + peso);
    if (p.color && !colorDeCausal.has(p.causal)) colorDeCausal.set(p.causal, p.color);
  }
  const top = [...porCausal.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([c]) => c);
  const causales = porCausal.size > top.length ? [...top, OTRAS] : top;
  const fillDe = (causal: string, i: number): string =>
    colorDeCausal.get(causal) ?? FALLBACK_FILLS[i % FALLBACK_FILLS.length]!;

  const porBucket = new Map<string, Record<string, number | string>>();
  for (const p of puntos) {
    const causal = top.includes(p.causal) ? p.causal : OTRAS;
    const fila = porBucket.get(p.bucket) ?? {
      bucket: p.bucket,
      label: labelDe(p.bucket),
      total: 0,
    };
    const valor = conCostos ? (p.valor_cop ?? 0) : p.minutos;
    fila[causal] = (Number(fila[causal]) || 0) + valor;
    fila.total = (Number(fila.total) || 0) + valor;
    porBucket.set(p.bucket, fila);
  }
  const data = [...porBucket.values()].sort((a, b) =>
    String(a.bucket).localeCompare(String(b.bucket)),
  );

  if (data.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-fg-tertiary">
        Sin paros que afecten disponibilidad en el período
      </div>
    );
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={210}>
        <ComposedChart
          data={data}
          margin={{ top: 20, right: 0, left: -8, bottom: 0 }}
          barCategoryGap="24%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
            axisLine={{ stroke: 'var(--color-border)' }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v) => (conCostos ? copM(Number(v)) : horas(Number(v)))}
            tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
            axisLine={false}
            tickLine={false}
            width={56}
          />
          <Tooltip
            cursor={{ fill: 'var(--color-surface-overlay)' }}
            contentStyle={{
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              fontSize: 12,
            }}
            formatter={(v, name) => [conCostos ? copM(Number(v)) : horas(Number(v)), String(name)]}
          />
          {causales.map((c, i) => (
            <Bar
              key={c}
              dataKey={c}
              stackId="paros"
              name={c}
              fill={fillDe(c, i)}
              maxBarSize={44}
              radius={i === causales.length - 1 ? [2, 2, 0, 0] : undefined}
            />
          ))}
          {/* Total del stack: línea invisible cuyo Y es el total exacto */}
          <Line
            dataKey="total"
            stroke="none"
            dot={false}
            activeDot={false}
            legendType="none"
            tooltipType="none"
            isAnimationActive={false}
          >
            <LabelList
              dataKey="total"
              position="top"
              offset={8}
              formatter={(v: unknown) =>
                Number(v) > 0 ? (conCostos ? copM(Number(v)) : horas(Number(v))) : ''
              }
              style={{ fontSize: 11, fill: 'var(--color-fg)', fontWeight: 700 }}
            />
          </Line>
        </ComposedChart>
      </ResponsiveContainer>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {causales.map((c, i) => (
          <span key={c} className="inline-flex items-center gap-1.5 text-[11px] text-fg-secondary">
            <span
              className="inline-block size-2.5 rounded-[2px]"
              style={{ background: fillDe(c, i) }}
            />
            {c}
          </span>
        ))}
      </div>
    </div>
  );
}
